import React, { useEffect, useState } from "react";
import { BookOpen, X } from "lucide-react";
import { apiGet } from "../../processes/helpers/api";
import { difficultyLabels, questionTypeTag, stripHtml } from "./teacherMocksHelpers";
import { Badge } from "./TeacherMocksShared";

function ChoiceRow({ letter, content, correct, size = "normal" }) {
  const html = stripHtml(content || "").replace(/\s+/g, " ").trim();
  return (
    <div className={`qpm-choice${correct ? " correct" : ""}${size === "small" ? " small" : ""}`}>
      <span className="qpm-choice-letter">{letter}</span>
      <span className="qpm-choice-text">{html}</span>
    </div>
  );
}

function StemBlock({ stem, index, isCase }) {
  const choices = (stem.choices || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const stemHtml = stem.content || "";

  return (
    <div className={`qpm-stem${index > 0 ? " has-gap" : ""}`}>
      {isCase && (
        <p className="qpm-stem-label">Questão {index + 1}</p>
      )}
      <div
        className="qpm-stem-text"
        dangerouslySetInnerHTML={{ __html: stemHtml }}
      />
      <div className="qpm-choices">
        {choices.map((choice, i) => (
          <ChoiceRow
            key={choice.id || i}
            letter={String.fromCharCode(65 + i)}
            content={choice.content}
            correct={choice.correct}
            size={isCase ? "small" : "normal"}
          />
        ))}
      </div>
    </div>
  );
}

export default function QuestionPreviewModal({ questionId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!questionId) return;
    let mounted = true;
    setLoading(true);
    setDetail(null);
    apiGet(`/api/v2/manager/questions/${questionId}`)
      .then((res) => {
        if (mounted) {
          setDetail(res.body?.data || null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [questionId]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!questionId) return null;

  const qId = `Q-${String(questionId).padStart(4, "0")}`;
  const meta = detail?.meta || {};
  const stems = meta.questionStems || [];
  const typeTag = detail ? questionTypeTag(detail) : null;
  const diffLabel = detail?.difficultyLevel ? difficultyLabels[detail.difficultyLevel] : null;
  const context = detail?.context || "";
  const isCase = stems.length > 1;

  return (
    <div className="qpm-overlay" onMouseDown={onClose}>
      <div
        className="qpm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Visualização da questão"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="qpm-header">
          <div className="qpm-header-left">
            <Badge variant="blue">{qId}</Badge>
            <span className="qpm-header-title">Visualização da questão</span>
          </div>
          <button type="button" className="qpm-close" aria-label="Fechar" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="qpm-body">
          {loading && <div className="qpm-state">Carregando questão...</div>}

          {!loading && !detail && (
            <div className="qpm-state">Não foi possível carregar a questão.</div>
          )}

          {!loading && detail && (
            <>
              <div className="qpm-badges">
                {typeTag && (
                  <span className={`teacher-mocks-create-question-pill ${typeTag.className}`}>
                    {typeTag.label}
                  </span>
                )}
                {diffLabel && detail?.difficultyLevel && (
                  <span className={`teacher-mocks-create-question-pill difficulty-${detail.difficultyLevel}`}>
                    {diffLabel}
                  </span>
                )}
              </div>

              {context && (
                <div className="qpm-context">
                  <p className="qpm-context-label">
                    <BookOpen size={13} aria-hidden="true" />
                    Texto base
                  </p>
                  <div
                    className="qpm-context-text"
                    dangerouslySetInnerHTML={{ __html: context }}
                  />
                </div>
              )}

              {stems.map((stem, i) => (
                <StemBlock
                  key={stem.id || i}
                  stem={stem}
                  index={i}
                  isCase={isCase}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
